import { listEmbodiments, type Embodiment, type EmbodimentListQuery } from "@/api";
import { normalizeWhitespace } from "@/utils";
import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import {
  createEmbodimentTableRows,
  type EmbodimentTableRow,
  type EmbodimentTableRowClickHandler,
} from "./EmbodimentTableRow";

type EmbodimentTableSourceOptions = {
  pageSize: number;
  rowClickHandler: EmbodimentTableRowClickHandler;
  resolveErrorMessage: (error: unknown) => string;
  // URL 에서 읽은 첫 검색어. 첫 로드에 바로 적용한다. 그래서 initialize 뒤 setSearchQuery 가 요청을
  // 한 번 더 보내지 않는다.
  initialSearchQuery?: string;
};

// 서버 contains 매칭은 대소문자를 구분하지 않는다. 같은 조건으로 다시 요청하지 않도록 여기서 먼저 정규화한다.
function normalizeSearchQuery(query: string): string {
  return normalizeWhitespace(query).toLowerCase();
}

export class EmbodimentTableSource extends DataTableSource<EmbodimentTableRow> {
  private readonly rowClickHandler: EmbodimentTableRowClickHandler;
  private searchQuery = "";

  constructor({
    pageSize,
    rowClickHandler,
    resolveErrorMessage,
    initialSearchQuery = "",
  }: EmbodimentTableSourceOptions) {
    super({ initialPageSize: pageSize, resolveErrorMessage });
    this.rowClickHandler = rowClickHandler;
    this.searchQuery = normalizeSearchQuery(initialSearchQuery);
  }

  createRow(embodiment: Embodiment): EmbodimentTableRow {
    return createEmbodimentTableRows([embodiment], this.rowClickHandler)[0];
  }

  setSearchQuery(query: string): Promise<void> {
    const nextQuery = normalizeSearchQuery(query);
    if (nextQuery === this.searchQuery) {
      return Promise.resolve();
    }

    this.searchQuery = nextQuery;
    return this.goToPage(1);
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<EmbodimentTableRow>> {
    // 검색을 서버에서 처리한다. 그래서 카탈로그 전체를 받지 않는다(서버사이드 name/model/tag contains).
    const query: EmbodimentListQuery = { page: req.page, size: req.size };
    if (this.searchQuery !== "") {
      query.search = this.searchQuery;
    }

    const response = await listEmbodiments(query, { signal });
    return {
      rows: createEmbodimentTableRows(response.items, this.rowClickHandler),
      total: response.total,
      page: response.page,
      size: response.size,
    };
  }
}
